// Side-channel hook for the vol-surface DVOL column. DVOL is computed backend-
// side via `market_DVOL_calc`; the FE only issues the request and displays the
// result (no methodology reconstructed here).
//
// HARD contract (IM-dvol): this is a best-effort enhancement layered on top of
// the surface table and must NEVER affect it. The hook therefore:
//   - issues its own JSON-RPC request, entirely separate from the table's
//     SnapshotContext/socketEvent data path;
//   - wraps every step in try/catch and a timeout, so ANY error, unexpected
//     shape or hang resolves to dvolByTenor = {} + an error flag, never a throw;
//   - sends nothing at all when the token is not BTC/ETH, there are no tenors,
//     or the socket is down — the column just renders blank.
// Its loading/error state is for the column alone and must not gate the table.
import { useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../../../../providers/websocket-context/websocket-provider';

import { parseDvolResponse, resolveDvolSymbol } from './dvol-utils';

const TIMEOUT_MS = 15_000;
// Hard ceiling on retry-drop-largest attempts so a pathological response (every
// attempt still -32603s) can't spin: a handful of drops is enough to clear the
// one-or-two out-of-range tenors seen in practice, and the 15s budget below is
// the real stop regardless.
const MAX_RETRIES = 6;
// The all-or-nothing trap code: `market_DVOL_calc` returns -32603 for the WHOLE
// request if ANY target tenor is at/beyond the longest listed Deribit expiry.
// Only this error is recoverable by dropping the largest tenor; anything else
// (e.g. -32602 missing config) is structural, so we blank immediately.
const ELIMINABLE_ERROR_CODE = -32603;

const extractErrorCode = (error: unknown): number | undefined => {
  if (typeof error === 'number') return error;
  if (error && typeof error === 'object') {
    const code = (error as Record<string, unknown>).code;
    if (typeof code === 'number') return code;
  }
  return undefined;
};

export interface UseDvolResult {
  dvolByTenor: Record<number, number>;
  loading: boolean;
  error: string | null;
}

export const useDvol = (
  token: string | null | undefined,
  pinnedReference: Date | null | undefined,
  tenors: number[],
): UseDvolResult => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);

  const [dvolByTenor, setDvolByTenor] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // WS context hands back fresh function refs each render; capture in a ref so
  // the fetch effect isn't re-fired by identity churn.
  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  const symbol = resolveDvolSymbol(token);
  const pinMs =
    pinnedReference && !isNaN(pinnedReference.getTime()) ? pinnedReference.getTime() : null;
  // Primitive dependency key so the effect only re-fires on a genuine change of
  // inputs, not on array/Date identity churn.
  const tenorsKey = tenors.join(',');

  useEffect(() => {
    // Reset on any input change so a stale column never lingers.
    setDvolByTenor({});
    setError(null);

    // Nothing to do: unsupported token, no reference, no tenors, or no socket.
    // Render blank, issue no request.
    if (!symbol || pinMs == null || tenors.length === 0 || !isConnected) {
      setLoading(false);
      return;
    }

    const ws = wsRef.current;
    if (!ws.sendMessage || !ws.subscribeToChannel) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let settled = false;
    // Every reqId we subscribe over the life of this effect (one per attempt).
    // We unsubscribe all of them on settle/teardown so a drop-largest retry
    // never leaks the superseded subscriptions.
    const usedReqIds: string[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;

    const unsubAll = () => {
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (!unsub) return;
      for (const id of usedReqIds) {
        try {
          unsub(id);
        } catch {
          /* ignore */
        }
      }
    };

    // Terminal resolutions. Both are idempotent (guarded by `settled`) and clear
    // the shared timeout budget, so a late socket callback after resolution is a
    // no-op and can never re-touch the column.
    const resolveBlank = (message: string | null) => {
      if (cancelled || settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      setError(message);
      setDvolByTenor({});
      setLoading(false);
      unsubAll();
    };

    const resolveData = (map: Record<number, number>) => {
      if (cancelled || settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      setDvolByTenor(map);
      setError(null);
      setLoading(false);
      unsubAll();
    };

    // One request attempt. `attemptsLeft` is the remaining retry budget (>=1).
    // `currentTenors` is sorted ascending, so the largest is the last element.
    const sendAttempt = (currentTenors: number[], attemptsLeft: number) => {
      if (cancelled || settled) return;
      if (currentTenors.length === 0) {
        // Dropped everything and still no success: converge to blank.
        resolveBlank(null);
        return;
      }

      const reqId = `DVOL_${symbol}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      usedReqIds.push(reqId);

      ws.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled || settled) return;
        try {
          const resp =
            raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
          if (resp?.error) {
            const code = extractErrorCode(resp.error);
            // -32603 is the all-or-nothing trap: the longest tenor is out of
            // range and fails the whole request. Drop it and retry, as long as
            // we have budget AND more than one tenor to shrink to.
            if (code === ELIMINABLE_ERROR_CODE && attemptsLeft > 1 && currentTenors.length > 1) {
              sendAttempt(currentTenors.slice(0, -1), attemptsLeft - 1);
              return;
            }
            // Non-eliminable error, no budget left, or a lone tenor that still
            // errors: blank the column, leave the table untouched.
            resolveBlank(`market_DVOL_calc error: ${JSON.stringify(resp.error)}`);
            return;
          }
          // Partial responses are fine: parseDvolResponse keeps whatever finite
          // tenors are present and silently omits the rest.
          resolveData(parseDvolResponse(raw, symbol));
        } catch (e) {
          resolveBlank(`market_DVOL_calc parse failure: ${String(e)}`);
        }
      });

      ws.sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: reqId,
          method: 'market_DVOL_calc',
          params: {
            // ISO pinned to 08:00:00 UTC (Deribit settle).
            timestamp: new Date(pinMs).toISOString(),
            tokens: [symbol],
            tenors: currentTenors,
            // REQUIRED — omitting config is a -32602 error, not a default.
            config: {},
          },
        }),
      );
    };

    try {
      setLoading(true);

      // Single budget across ALL retries — a chain of drop-largest retries can
      // never run longer in aggregate than one request was allowed to.
      timer = setTimeout(() => {
        resolveBlank('market_DVOL_calc timed out');
      }, TIMEOUT_MS);

      // Ascending copy so slice(0, -1) always drops the largest remaining tenor.
      const sorted = [...tenors].sort((a, b) => a - b);
      sendAttempt(sorted, Math.min(sorted.length, MAX_RETRIES));
    } catch (e) {
      // Any synchronous failure issuing the request: blank and continue.
      resolveBlank(`market_DVOL_calc request failure: ${String(e)}`);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      unsubAll();
    };
  }, [symbol, pinMs, tenorsKey, isConnected]);

  return { dvolByTenor, loading, error };
};
